/* eslint-disable max-lines */

import { Dir, Direction } from '@angular/cdk/bidi';
import {
  ConnectedOverlayPositionChange,
  ConnectionPositionPair,
  FlexibleConnectedPositionStrategy,
  Overlay,
  OverlayConfig,
  OverlayRef,
} from '@angular/cdk/overlay';
import { ComponentPortal, TemplatePortal } from '@angular/cdk/portal';
import { ScrollDispatcher } from '@angular/cdk/scrolling';
import {
  ChangeDetectorRef,
  ComponentRef,
  Directive,
  ElementRef,
  EmbeddedViewRef,
  EventEmitter,
  Host,
  Inject,
  Input,
  NgZone,
  OnDestroy,
  Optional,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';

import { fromEvent as observableFromEvent, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { asViewRef } from '@recruitee/utils';

import { OverlayClipArea } from '../../component-tooltip/types';

// First word described where the dropdown will land,
// Second word describes its align
// F.e. "BottomLeft" means menu will appear in the bottom and will be aligned to the left
// F.e. "RightTop" means menu will appear in the right and will be aligned to the top
export enum OverlayPosition {
  BottomLeft = 'bottomleft',
  BottomRight = 'bottomright',
  TopLeft = 'topleft',
  TopRight = 'topright',
  RightBottom = 'rightbottom',
  RightTop = 'righttop',
  LeftBottom = 'leftbottom',
  LeftTop = 'lefttop',
}

/**
 * Directive applied to an element to make it usable as an origin for an Overlay using a
 * ConnectedPositionStrategy.
 */
@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: '[rt-overlay-origin]',
  exportAs: 'rtOverlayOrigin',
  standalone: true,
})
export class OverlayOriginDirective {
  constructor(public elementRef: ElementRef) {}
}

/** Default set of positions for the overlay. Follows the behavior of a dropdown. */
export const positionPairs = {
  [OverlayPosition.BottomLeft]: new ConnectionPositionPair(
    { originX: 'start', originY: 'bottom' },
    { overlayX: 'start', overlayY: 'top' },
  ),
  [OverlayPosition.BottomRight]: new ConnectionPositionPair(
    { originX: 'end', originY: 'bottom' },
    { overlayX: 'end', overlayY: 'top' },
  ),
  [OverlayPosition.TopLeft]: new ConnectionPositionPair(
    { originX: 'start', originY: 'top' },
    { overlayX: 'start', overlayY: 'bottom' },
  ),
  [OverlayPosition.TopRight]: new ConnectionPositionPair(
    { originX: 'end', originY: 'top' },
    { overlayX: 'end', overlayY: 'bottom' },
  ),
  [OverlayPosition.LeftBottom]: new ConnectionPositionPair(
    { originX: 'start', originY: 'bottom' },
    { overlayX: 'end', overlayY: 'bottom' },
  ),
  [OverlayPosition.LeftTop]: new ConnectionPositionPair(
    { originX: 'start', originY: 'top' },
    { overlayX: 'end', overlayY: 'top' },
  ),
  [OverlayPosition.RightBottom]: new ConnectionPositionPair(
    { originX: 'end', originY: 'bottom' },
    { overlayX: 'start', overlayY: 'bottom' },
  ),
  [OverlayPosition.RightTop]: new ConnectionPositionPair(
    { originX: 'end', originY: 'top' },
    { overlayX: 'start', overlayY: 'top' },
  ),
};

const defaultPositions = {
  [OverlayPosition.BottomLeft]: [
    positionPairs[OverlayPosition.BottomLeft],
    positionPairs[OverlayPosition.TopLeft],
  ],
  [OverlayPosition.BottomRight]: [
    positionPairs[OverlayPosition.BottomRight],
    positionPairs[OverlayPosition.TopRight],
  ],
  [OverlayPosition.TopLeft]: [
    positionPairs[OverlayPosition.TopLeft],
    positionPairs[OverlayPosition.BottomLeft],
  ],
  [OverlayPosition.TopRight]: [
    positionPairs[OverlayPosition.TopRight],
    positionPairs[OverlayPosition.BottomRight],
  ],
  [OverlayPosition.RightBottom]: [
    positionPairs[OverlayPosition.RightBottom],
    positionPairs[OverlayPosition.RightTop],
  ],
  [OverlayPosition.RightTop]: [
    positionPairs[OverlayPosition.RightTop],
    positionPairs[OverlayPosition.RightBottom],
  ],
  [OverlayPosition.LeftBottom]: [
    positionPairs[OverlayPosition.LeftBottom],
    positionPairs[OverlayPosition.LeftTop],
  ],
  [OverlayPosition.LeftTop]: [
    positionPairs[OverlayPosition.LeftTop],
    positionPairs[OverlayPosition.LeftBottom],
  ],
};

/**
 * Directive to facilitate declarative creation of an Overlay using a ConnectedPositionStrategy.
 */
@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: '[rt-connected-overlay]',
  exportAs: 'rtConnectedOverlay',
  standalone: true,
  providers: [Overlay],
})
export class ConnectedOverlayDirective implements OnDestroy {
  // Registered connected position pairs.
  @Input()
  public set positions(v: ConnectionPositionPair[]) {
    this._positions = v;
  }

  public get positions(): ConnectionPositionPair[] {
    return this._positions && this._positions.length
      ? this._positions
      : Array.isArray(this.position)
        ? this.position.reduce(
            (agg, positionKey: OverlayPosition) => [...agg, ...defaultPositions[positionKey]],
            [],
          )
        : defaultPositions[this.position];
  }

  /** The offset in pixels for the overlay connection point on the x-axis */
  @Input()
  public get offsetX(): number {
    return this._offsetX;
  }

  public set offsetX(offsetX: number) {
    this._offsetX = offsetX;
    if (this._position) {
      this._position.withDefaultOffsetX(offsetX);
    }
  }

  /** The offset in pixels for the overlay connection point on the y-axis */
  @Input()
  public get offsetY(): number {
    return this._offsetY;
  }

  public set offsetY(offsetY: number) {
    this._offsetY = offsetY;
    if (this._position) {
      this._position.withDefaultOffsetY(offsetY);
    }
  }

  /** Whether or not the overlay should attach a backdrop. */
  public static ngAcceptInputType_hasBackdrop: boolean | '';

  @Input()
  public get hasBackdrop(): boolean {
    return this._hasBackdrop;
  }

  // TODO: move the boolean coercion logic to a shared function in core
  public set hasBackdrop(value: boolean) {
    this._hasBackdrop = value != null && `${value}` !== 'false';
  }

  /** Wheter or not the overlay should stay in place on scroll **/
  @Input()
  public get isPinned(): boolean {
    return this._isPinned;
  }

  public set isPinned(value: boolean) {
    this._isPinned = value != null && `${value}` !== 'false';
  }

  @Input()
  public get open(): boolean {
    return this._open;
  }

  public set open(value: boolean) {
    if (!(value && this._overlayRef?.hasAttached()) && this.open !== value) {
      this.toggleOverlay(value);
    }
  }

  public get positionStrategy(): FlexibleConnectedPositionStrategy {
    return this._position;
  }

  public set portal(portal: TemplatePortal | ComponentPortal<any>) {
    this._templatePortal = portal;
  }

  /** The associated overlay reference. */
  public get overlayRef(): OverlayRef {
    return this._overlayRef!;
  }

  /** The element's layout direction. */
  public get dir(): Direction {
    return this._dir ? this._dir.value : 'ltr';
  }

  /** Origin for the connected overlay. */
  @Input() public origin: OverlayOriginDirective;

  @Input('rtConnectedOverlayPosition')
  public position: string[] | OverlayPosition = [
    OverlayPosition.BottomRight,
    OverlayPosition.BottomLeft,
    OverlayPosition.TopRight,
    OverlayPosition.TopLeft,
  ];

  @Input('rtConnectedOverlayAutoClose') public autoCloseOnUnpreventedClick: boolean = true;

  /** Defines clip area for tooltip overlay */
  @Input() public overlayClipArea?: OverlayClipArea;

  /** The width of the overlay panel. */
  @Input() public width: number | string;

  /** The height of the overlay panel. */
  @Input() public height: number | string;

  /** The min width of the overlay panel. */
  @Input() public minWidth: number | string;

  /** The min height of the overlay panel. */
  @Input() public minHeight: number | string;

  /** The custom class to be set on the backdrop element. */
  @Input() public backdropClass: string;

  /** Event emitted when the backdrop is clicked. */
  @Output() public backdropClick: EventEmitter<void> = new EventEmitter<void>();

  /** Event emitted when the component is clicked. */
  @Output() public componentAutoCloseClick: EventEmitter<void> = new EventEmitter<void>();

  /** Event emitted when the position has changed. */
  @Output()
  public positionChange: EventEmitter<ConnectedOverlayPositionChange> =
    new EventEmitter<ConnectedOverlayPositionChange>();

  /** Event emitted when the overlay has been attached. */
  @Output() public attach: EventEmitter<void> = new EventEmitter<void>();

  /** Event emitted when the overlay has been detached. */
  @Output() public detach: EventEmitter<void> = new EventEmitter<void>();
  private _overlayRef: OverlayRef | null;
  private _templatePortal: TemplatePortal | ComponentPortal<any>;
  private _open: boolean = false;
  private _hasBackdrop: boolean = false;
  private _isPinned: boolean = true;
  private _backdropSubscription: Subscription | null;
  private _positionSubscription: Subscription | null;
  private _offsetX: number = 0;
  private _offsetY: number = 0;
  private _position: FlexibleConnectedPositionStrategy;

  private _positions: ConnectionPositionPair[];

  private scrollSubscription: Subscription;

  // TODO (jzielinski) animations

  constructor(
    @Inject(TemplateRef)
    public templateRef: TemplateRef<any> | null,
    public viewContainerRef: ViewContainerRef,
    private _overlay: Overlay,
    private cdRef: ChangeDetectorRef,
    private ngZone: NgZone,
    @Host() private parentCdRef: ChangeDetectorRef,
    @Optional() private scrollDispatcher: ScrollDispatcher,
    @Optional() private _dir: Dir,
  ) {
    if (templateRef) {
      this._templatePortal = new TemplatePortal(templateRef, viewContainerRef);
    }
  }

  public ngOnDestroy(): void {
    this.destroyOverlay();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    const { width, height } = changes;

    if ((width || height) && this._overlayRef) {
      this._overlayRef.updateSize({ width: this.width, height: this.height });
      this.updatePositionWithoutRedrawing();
    }
  }

  public openAt(origin: OverlayOriginDirective): void {
    this.origin = origin;
    this.toggleOverlay(true);
  }

  public toggleOverlay(value: false): void;
  public toggleOverlay(value: true): EmbeddedViewRef<any> | ComponentRef<any>;
  public toggleOverlay(value: boolean): EmbeddedViewRef<any> | ComponentRef<any> | void;
  public toggleOverlay(value: boolean): EmbeddedViewRef<any> | ComponentRef<any> | void {
    const result = value ? this._attachOverlay() : this._detachOverlay();
    this._open = value;
    if (!asViewRef(this.parentCdRef).destroyed) {
      this.parentCdRef.detectChanges();
      this.updatePositionWithoutRedrawing();
    }
    return result;
  }

  public recreateOverlay(): void {
    this.destroyOverlay();
    this._createOverlay();
  }

  public updatePosition(): void {
    if (this.open) {
      // TODO instead of recreating the overlay, just update its' position in-place
      this.open = false;
      this.open = true;
    }
  }

  public updatePositionWithoutRedrawing(): void {
    if (this.open && this._overlayRef && this._position['_pane']) {
      this._overlayRef.updateSize({});
      this._overlayRef.updatePosition();
    }
  }

  /** Destroys the overlay created by this directive. */
  public destroyOverlay(): void {
    if (this._overlayRef) {
      this._overlayRef.dispose();
      this._overlayRef = null;
    }

    if (this._backdropSubscription) {
      this._backdropSubscription.unsubscribe();
      this._backdropSubscription = null;
    }
    if (this._positionSubscription) {
      this._positionSubscription.unsubscribe();
      this._positionSubscription = null;
    }
  }

  /** Creates an overlay */
  private _createOverlay(): void {
    this._overlayRef = this._overlay.create(this._buildConfig());
  }

  /** Builds the overlay config based on the directive's inputs */
  // eslint-disable-next-line max-lines-per-function
  private _buildConfig(): OverlayConfig {
    const overlayConfig = new OverlayConfig();

    if (this.width || this.width === 0) {
      overlayConfig.width = this.width;
    }

    if (this.height || this.height === 0) {
      overlayConfig.height = this.height;
    }

    if (this.minWidth || this.minWidth === 0) {
      overlayConfig.minWidth = this.minWidth;
    }

    if (this.minHeight || this.minHeight === 0) {
      overlayConfig.minHeight = this.minHeight;
    }

    overlayConfig.hasBackdrop = this.hasBackdrop;

    if (this.backdropClass) {
      overlayConfig.backdropClass = ['rt-overlay-backdrop', this.backdropClass];
    } else {
      overlayConfig.backdropClass = ['rt-overlay-backdrop', 'blank-backdrop'];
    }

    if (!this.origin) {
      throw new Error(
        // eslint-disable-next-line @typescript-eslint/no-base-to-string
        `.origin prop seems to be missing in component with ConnectedOverlayDirective: ${this}`,
      );
    }

    this._position = <FlexibleConnectedPositionStrategy>this._createPositionStrategy();
    overlayConfig.positionStrategy = this._position;

    overlayConfig.direction = this.dir;

    return overlayConfig;
  }

  /** Returns the position strategy of the overlay to be set on the overlay config */
  private _createPositionStrategy(): FlexibleConnectedPositionStrategy {
    const pos = this.positions[0];
    const originPoint = { originX: pos.originX, originY: pos.originY };
    const overlayPoint = { overlayX: pos.overlayX, overlayY: pos.overlayY };
    const strategy = this._overlay
      .position()
      .flexibleConnectedTo(this.origin.elementRef)
      .withPositions([{ ...originPoint, ...overlayPoint }])
      .withDefaultOffsetX(this.offsetX)
      .withDefaultOffsetY(this.offsetY);

    this._handlePositionChanges(strategy);

    return strategy;
  }

  private _handlePositionChanges(strategy: FlexibleConnectedPositionStrategy): void {
    strategy.withPositions([
      ...strategy.positions,
      ...this.positions.map(pos => ({
        originX: pos.originX,
        originY: pos.originY,
        overlayX: pos.overlayX,
        overlayY: pos.overlayY,
      })),
    ]);

    this._positionSubscription = strategy.positionChanges.subscribe(pos =>
      this.positionChange.emit(pos),
    );
  }

  /** Attaches the overlay and subscribes to backdrop clicks if backdrop exists
   Also subscribes to scroll if in pinned mode,
   */
  // eslint-disable-next-line max-lines-per-function
  private _attachOverlay(): EmbeddedViewRef<any> | ComponentRef<any> {
    if (!this._overlayRef) {
      this._createOverlay();
    }

    if (this.hasBackdrop && this._overlayRef) {
      this._backdropSubscription = this._overlayRef.backdropClick().subscribe(() => {
        this.backdropClick.emit();
      });
    }

    if (this.scrollDispatcher && this.isPinned) {
      this.scrollSubscription = this.ngZone.runOutsideAngular(() => {
        let tick = false;

        return this.scrollDispatcher.scrolled(0).subscribe(() => {
          if (this._overlayRef) {
            if (!tick) {
              tick = true;
              requestAnimationFrame(() => {
                if (this.overlayClipArea) {
                  this.handleTooltipOverlayClipping();
                }
                this._overlayRef?.updatePosition();
                tick = false;
              });
            }
          }
        });
      });
    }

    if (this.autoCloseOnUnpreventedClick && this._overlayRef) {
      observableFromEvent(this._overlayRef.overlayElement, 'click')
        .pipe(takeUntil(this.detach.asObservable()))
        .subscribe((event: MouseEvent) => {
          if (!event.defaultPrevented) {
            this.open = false;
            this.componentAutoCloseClick.emit();
          }
          // @ts-ignore
          if (!this.cdRef['destroyed']) {
            this.cdRef.detectChanges();
          }
          return event;
        });
    }

    const result = this._overlayRef!.attach(
      <ComponentPortal<any> | TemplatePortal>this._templatePortal,
    );

    this.ngZone.runOutsideAngular(() =>
      requestAnimationFrame(() => {
        this._overlayRef!.updatePosition();
        // @ts-ignore
        if (!this.cdRef['destroyed']) {
          this.cdRef.detectChanges();
        }
      }),
    );

    this.attach.emit();

    return result;
  }

  private handleTooltipOverlayClipping(): void {
    const tooltipOverlayContainer = this._overlayRef!.overlayElement.parentElement!;

    const {
      top: containerTop,
      right: containerRight,
      bottom: containerBottom,
      left: containerLeft,
    } = tooltipOverlayContainer.getBoundingClientRect();

    const [topOverlayClip, rightOverlayClip, bottomOverlayClip, leftOverlayClip] =
      this.overlayClipArea!;

    const clipTop = topOverlayClip ? `${(containerTop - topOverlayClip) * -1}px` : '0';
    const clipRight = rightOverlayClip ? `${containerRight - rightOverlayClip}px` : '100%';
    const clipBottom = bottomOverlayClip ? `${containerBottom - bottomOverlayClip}px` : '100%';
    const clipLeft = leftOverlayClip ? `${(containerLeft - leftOverlayClip) * -1}px` : '0';

    tooltipOverlayContainer.style.clipPath = `polygon(${clipLeft} ${clipTop}, ${clipRight} ${clipTop}, ${clipRight} ${clipBottom}, ${clipLeft} ${clipBottom})`;
  }

  /** Detaches the overlay and unsubscribes to backdrop clicks if backdrop exists */
  private _detachOverlay(): void {
    if (this.scrollSubscription) {
      this.scrollSubscription.unsubscribe();
    }

    if (this._overlayRef) {
      this._overlayRef.detach();
      this.detach.emit();
    }

    if (this._backdropSubscription) {
      this._backdropSubscription.unsubscribe();
      this._backdropSubscription = null;
    }
  }
}
